import { type FilterPredicates, FilterOperator } from '@/contexts/shared/filters-predicate/FiltersPredicate.interfaces'

const hasFilterValue = <T>(value: T | undefined): value is T =>
  value !== undefined && value !== null && value !== '' && (!Array.isArray(value) || value.length > 0)

const toPredicateValues = <T>(value: T | undefined): T[] | undefined => (hasFilterValue(value) ? [value] : undefined)

export const buildInFilterPredicates = <T>(
  values: T[] | undefined
): FilterPredicates<T, FilterOperator.IN> | undefined => {
  if (!values?.length) {
    return undefined
  }

  return [{ operator: FilterOperator.IN, value: values }]
}

export const buildContainFilterPredicates = (
  value: string | undefined
): FilterPredicates<string, FilterOperator.CONTAINS> | undefined => {
  const formattedPredicateValue = toPredicateValues(value)
  if (!formattedPredicateValue) {
    return undefined
  }

  return [{ operator: FilterOperator.CONTAINS, value: formattedPredicateValue }]
}

export const buildRangeFilterPredicates = (
  minimum: number | undefined,
  maximum: number | undefined
): FilterPredicates<number, FilterOperator.GTE | FilterOperator.LTE> | undefined => {
  const predicates: FilterPredicates<number, FilterOperator.GTE | FilterOperator.LTE> = []
  const formattedMinValue = toPredicateValues(minimum)
  const formattedMaxValue = toPredicateValues(maximum)

  if (formattedMinValue !== undefined) {
    predicates.push({ operator: FilterOperator.GTE, value: formattedMinValue })
  }

  if (formattedMaxValue !== undefined) {
    predicates.push({ operator: FilterOperator.LTE, value: formattedMaxValue })
  }

  return predicates.length ? predicates : undefined
}

export const getInFilterPredicatesValue = <T, O extends FilterOperator>(
  predicates: FilterPredicates<T, O> | undefined,
  operator: FilterOperator = FilterOperator.IN
): T[] | undefined => predicates?.find((filterPredicate) => filterPredicate.operator === operator)?.value
